import React, { useState, useEffect } from 'react';
import { useTranslation } from '../Context/TranslationContext';
import { 
  X, Plus, Minus, ShoppingCart, ArrowLeft, 
  Truck, CreditCard, Gift, Clock, HelpCircle,
  ShieldCheck, Package 
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../apiConfig/api';
import { toast } from 'react-toastify';
import { useAuthStore } from '../Zustand/authStore';
import { useCartStore } from '../Zustand/cartStore';

const translations = {
  en: {
    title: 'Shopping Cart',
    empty: 'Your cart is empty',
    total: 'Total',
    totalItems: 'Total Items',
    subtotal: 'Subtotal',
    checkout: 'Proceed to Checkout',
    remove: 'Remove',
    continueShopping: 'Continue Shopping',
    estimatedShipping: 'Estimated Shipping',
    freeShipping: 'Free Shipping',
    tax: 'Tax',
    whyShopWithUs: 'Why Shop with Us?',
    fastShipping: 'Fast and reliable shipping',
    securePayment: 'Secure payment with multiple options',
    exclusiveDeals: 'Exclusive member discounts & offers',
    orderSummary: 'Order Summary',
    items: 'Items',
    estimatedDelivery: 'Estimated Delivery',
    days: 'days',
    category: 'Category',
    description: 'Description',
    quantity: 'Quantity',
    price: 'Price',
    faq: 'Frequently Asked Questions',
    faqQuestion1: 'How long will my order take to arrive?',
    faqAnswer1: 'Delivery times vary depending on your location, but typically orders arrive within 3-5 business days.',
    faqQuestion2: 'What is your return policy?',
    faqAnswer2: 'We offer a hassle-free 30-day return policy for most items. Please check our returns page for more details.',
    faqQuestion3: 'Do you offer international shipping?',
    faqAnswer3: 'Yes, we ship globally with competitive rates. International shipping costs and delivery times may vary by location.',
    errorFetchingCart: 'Unable to load your cart items. Please try again.',
    errorFetchingProducts: 'Error loading product details. Please refresh the page.',
    uncategorized: 'Uncategorized',
    noDescription: 'No description available',
    unnamedProduct: 'Unnamed Product',
    deleteProduct: 'Remove Item',
    productDeleted: 'Item removed from cart successfully',
    errorDeleting: 'Error removing item from cart',
    priceDetails: 'Price Details',
    shippingAndHandling: 'Shipping & Handling',
    guaranteedDelivery: 'Guaranteed Delivery',
    moneyBackGuarantee: '100% Money Back Guarantee',
    secureCheckout: 'Secure Checkout',
    customerSupport: '24/7 Customer Support',
    updateQuantity: 'Update Quantity'
  },
  ta: {
    title: 'கடை கூட்டு',
    empty: 'உங்கள் கடை கூட்டு காலி',
    total: 'மொத்தம்',
    totalItems: 'மொத்த உருப்படிகள்',
    subtotal: 'துணை மொத்தம்',
    checkout: 'சரிபார்ப்புக்கு செல்க',
    remove: 'அகற்று',
    continueShopping: 'தொடர்ந்து வாங்குக',
    estimatedShipping: 'மதிப்பிடப்பட்ட விநியோகம்',
    freeShipping: 'இலவச விநியோகம்',
    tax: 'வரி',
    whyShopWithUs: 'ஏன் எங்களுடன் சிறப்பு?',
    fastShipping: 'விரைவான மற்றும் நம்பகமான விநியோகம்',
    securePayment: 'பல விருப்பங்களுடன் பாதுகாப்பான கட்டணம்',
    exclusiveDeals: 'உறுப்பினர் தள்ளுபடிகள் மற்றும் வழங்கல்கள்',
    orderSummary: 'ஆர்டர் சுருக்கம்',
    items: 'உருப்படிகள்',
    estimatedDelivery: 'மதிப்பிடப்பட்ட விநியோகம்',
    days: 'நாட்கள்',
    category: 'வகை',
    description: 'விளக்கம்',
    quantity: 'அளவு',
    price: 'விலை',
    faq: 'அடிக்கடி கேட்கப்படும் கேள்விகள்',
    faqQuestion1: 'எவ்வளவு நேரத்தில் என் ஆர்டர் வரும்?',
    faqAnswer1: 'உங்கள் இருப்பிடத்தைப் பொறுத்து விநியோக நேரம் மாறுபடும், ஆனால் பொதுவாக ஆர்டர்கள் 3-5 வணிக நாட்களில் வருகின்றன.',
    faqQuestion2: 'உங்கள்반환 கொள்கை என்ன?',
    faqAnswer2: 'பெரும்பான்மையான உருப்படிகளுக்கு எங்களிடம் சிக்கலற்ற 30 நாள்반환 கொள்கை உள்ளது. தயவுசெய்து எங்கள்반환 பக்கத்தைப் பார்க்கவும்.',
    faqQuestion3: 'நீங்கள் சர்வதேச விநியோகத்தை வழங்குகிறீர்களா?',
    faqAnswer3: 'ஆம், நாங்கள் போட்டித் தரிசுநிலையில் உலகளாவிய அளவில் அனுப்புகிறோம். சர்வதேச விநியோக செலவுகள் மற்றும் விநியோக நேரம் இடத்தைப் பொறுத்து மாறுபடும்.',
    errorFetchingCart: 'உங்கள் கடை கூட்டு உருப்படிகளை ஏற்ற முடியவில்லை. தயவுசெய்து மீண்டும் முயலவும்.',
    errorFetchingProducts: 'பொருள் விவரங்களை ஏற்ற பிழை. தயவுசெய்து பக்கத்தை புதுப்பிக்கவும்.',
    uncategorized: 'வகைப்படுத்தப்படாத',
    noDescription: 'விளக்கம் இல்லை',
    unnamedProduct: 'பெயரிடப்படாத பொருள்',
    deleteProduct: 'உருப்படியை அகற்று',
    productDeleted: 'கடை கூட்டிலிருந்து உருப்படி வெற்றிகரமாக அகற்றப்பட்டது',
    errorDeleting: 'கடை கூட்டிலிருந்து உருப்படியை அகற்றுவதில் பிழை',
    priceDetails: 'விலை விவரங்கள்',
    shippingAndHandling: 'விநியோகம் & கையாளுதல்',
    guaranteedDelivery: 'உத்தரவாதமான விநியோகம்',
    moneyBackGuarantee: '100% பணம் திரும்ப பெறும் உத்தரவாதம்',
    secureCheckout: 'பாதுகாப்பான சரிபார்ப்பு',
    customerSupport: '24/7 வாடிக்கையாளர் ஆதரவு',
    updateQuantity: 'அளவை புதுப்பி'
  }
};

// Shopping benefits data
const shoppingBenefits = [
  {
    icon: Truck,
    title: 'Fast Delivery',
    description: '3-5 business days delivery'
  },
  {
    icon: ShieldCheck,
    title: 'Secure Payment',
    description: 'Multiple payment options'
  },
  {
    icon: Package,
    title: 'Quality Assurance',
    description: 'Verified products'
  }
];

// Order process steps
const orderSteps = [
  {
    title: 'Add to Cart',
    description: 'Select your items'
  },
  {
    title: 'Checkout',
    description: 'Secure payment'
  },
  {
    title: 'Delivery',
    description: 'Fast shipping'
  }
];

// Add this with the other constants in Part 1, after shoppingBenefits and orderSteps

const FAQ = [
  {
    question: translations.en.faqQuestion1,
    answer: translations.en.faqAnswer1
  },
  {
    question: translations.en.faqQuestion2,
    answer: translations.en.faqAnswer2
  },
  {
    question: translations.en.faqQuestion3,
    answer: translations.en.faqAnswer3
  }
];

const Cart = () => {
  const navigate = useNavigate();
  const { isTamil } = useTranslation();
  const [showFAQ, setShowFAQ] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const { token, userId } = useAuthStore();
  const { cartWithProducts, setCartWithProducts } = useCartStore();
  const t = translations[isTamil ? 'ta' : 'en'];

// Continuing from Part 1...

  // API Configuration
  const configureAPI = () => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  };

  // Data management functions
  const refreshCartData = () => {
    setIsDataFetched(false);
  };
  const getImageUrl = (filepath) => {
    if (!filepath) {
      console.log('No filepath provided, using placeholder');
      return '/placeholder/400/320';
    }
  
    try {
      // If filepath is already just a filename, use it directly
      if (!filepath.includes('\\') && !filepath.includes('/')) {
        const imageUrl = `https://tnbsg.org/v1/uploads/${filepath}`;
        console.log('Direct filename:', filepath, 'URL:', imageUrl);
        return imageUrl;
      }
  
      // Otherwise, extract the filename from the path
      const filename = filepath.split(/[\/\\]/).pop();
      const imageUrl = `https://tnbsg.org/v1/uploads/${filename}`;
      console.log('Extracted filename from:', filepath, 'URL:', imageUrl);
      return imageUrl;
    } catch (error) {
      console.error('Error processing image URL:', error);
      return '/placeholder/400/320';
    }
  };
  
  const handleDeleteProduct = async (productId) => {
    try {
      configureAPI();
      await api.delete(`deleteCart?productId=${productId}&loginId=${userId}`);
      toast.success(t.productDeleted, {
        position: "top-right",
        autoClose: 2000,
        className: "bg-white"
      });
      refreshCartData();
    } catch (error) {
      console.error('Error deleting product:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', {
          position: "top-right",
          autoClose: 3000
        });
        useAuthStore.getState().clearAuth();
        navigate('/login');
      } else {
        toast.error(t.errorDeleting, {
          position: "top-right",
          autoClose: 3000
        });
      }
    }
  };

  // Fetch cart and product data
  useEffect(() => {
    const fetchCartAndProducts = async () => {
      if (isDataFetched || !token || !userId) {
        setLoading(false);
        if (!token) navigate('/login');
        return;
      }

      try {
        configureAPI();
        const cartResponse = await api.get(`/listCart?loginId=${userId}`);
        
        if (cartResponse.data && Array.isArray(cartResponse.data.results)) {
          const productPromises = cartResponse.data.results.map(async (cartItem) => {
            try {
              const productResponse = await api.get(`/listProduct?id=${cartItem.productId}`);
              return {
                cartItem,
                productDetails: productResponse.data.results
              };
            } catch (error) {
              console.error(`Error fetching product ${cartItem.productId}:`, error);
              return {
                cartItem,
                productDetails: null
              };
            }
          });

          const responses = await Promise.all(productPromises);
          
          const cartWithProductDetails = responses.map(({ cartItem, productDetails }) => ({
            id: cartItem.id,
            productId: cartItem.productId,
            quantity: cartItem.quantity,
            name: productDetails?.name || t.unnamedProduct,
            price: productDetails?.price || '0',
            category: productDetails?.category || t.uncategorized,
            description: productDetails?.description || t.noDescription,
            filepath: productDetails?.filepath || null
          })).filter(item => item.price > 0); // Filter out invalid items

          setCartWithProducts(cartWithProductDetails);
        }
      } catch (error) {
        console.error('Error fetching cart and products:', error);
        handleApiError(error);
      } finally {
        setLoading(false);
        setIsDataFetched(true);
      }
    };

    fetchCartAndProducts();
  }, [token, userId, setCartWithProducts, t, navigate, isDataFetched]);

  // Quantity management
  const handleDecreaseQuantity = async (item) => {
    if (item.quantity <= 1) {
      if (window.confirm(t.removeConfirmation)) {
        handleDeleteProduct(item.productId);
      }
      return;
    }

    try {
      configureAPI();
      await api.put(`/updateProductCount?loginId=${userId}&productId=${item.productId}&quantity=${item.quantity - 1}`);
      refreshCartData();
    } catch (error) {
      console.error('Error updating quantity:', error);
      handleApiError(error);
    }
  };

  const handleIncreaseQuantity = async (item) => {
    try {
      configureAPI();
      await api.put(`/updateProductCount?loginId=${userId}&productId=${item.productId}&quantity=${item.quantity + 1}`);
      refreshCartData();
    } catch (error) {
      console.error('Error updating quantity:', error);
      handleApiError(error);
    }
  };

  const handleApiError = (error) => {
    if (error.response?.status === 401) {
      toast.error('Session expired. Please login again.', {
        position: "top-right",
        autoClose: 3000
      });
      useAuthStore.getState().clearAuth();
      navigate('/login');
    } else {
      toast.error(error.message === 'Network Error' ? t.errorFetchingCart : t.errorFetchingProducts, {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  // Calculations
  const calculateSubtotal = () => {
    return cartWithProducts.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      return total + (price * quantity);
    }, 0);
  };

  const calculateTotalItems = () => {
    return cartWithProducts.reduce((total, item) => total + (parseInt(item.quantity) || 0), 0);
  };

  const getTaxAmount = () => {
    const subtotal = calculateSubtotal();
    return subtotal * 0.0; // 0% tax - adjust as needed
  };

  const getShippingCost = () => {
    return 0; // Free shipping
  };

  const getFinalTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = getTaxAmount();
    const shipping = getShippingCost();
    return subtotal + tax + shipping;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your cart...</p>
        </div>
      </div>
    );
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.2 }
    }
  };



  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 pt-36">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{t.title}</h1>
          {cartWithProducts.length > 0 && (
            <p className="text-gray-600">
              {calculateTotalItems()} {t.items}
            </p>
          )}
        </div>
  
        {(!cartWithProducts || cartWithProducts.length === 0) ? (
          // Empty Cart State - Centered Content
          <div className="max-w-3xl mx-auto">
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-xl shadow-md overflow-hidden mb-8"
            >
              <div className="p-12 text-center">
                <ShoppingCart className="w-24 h-24 text-gray-400 mx-auto mb-6" />
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t.empty}</h2>
                <p className="text-gray-600 mb-8 text-lg">Browse our collection and find something you love</p>
                <Link 
                  to="/product" 
                  className="inline-flex items-center px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 text-lg"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  {t.continueShopping}
                </Link>
              </div>
            </motion.div>
  
            {/* Shopping Benefits - Centered */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
            >
              {shoppingBenefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-white p-8 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow duration-300"
                >
                  <benefit.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-gray-900 mb-3 text-lg">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </motion.div>
  
            {/* FAQ Section - Centered */}
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-xl shadow-md p-8"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">{t.faq}</h2>
                <button
                  onClick={() => setShowFAQ(!showFAQ)}
                  className="text-blue-600 hover:text-blue-700 transition p-2 rounded-full hover:bg-blue-50"
                >
                  <HelpCircle className="w-6 h-6" />
                </button>
              </div>
  
              <AnimatePresence>
                {showFAQ && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {FAQ.map((item, index) => (
                      <div key={index} className="border-b pb-6 last:border-b-0 last:pb-0">
                        <h3 className="font-medium text-gray-900 mb-3 text-lg">{item.question}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        ) : (
          // Cart With Items - Left Aligned with Right Sidebar
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column - Cart Items */}
            <div className="lg:col-span-8">
              <motion.div
                variants={itemVariants}
                className="bg-white rounded-xl shadow-md overflow-hidden mb-8"
              >
                <div className="divide-y divide-gray-200">
                  {cartWithProducts.map((item) => (
                    <motion.div
                      key={item.id}
                      variants={itemVariants}
                      className="p-6 hover:bg-gray-50 transition duration-150"
                    >
                      <div className="flex flex-col md:flex-row gap-6">
                        {/* Product Image */}
                        <div className="w-full md:w-1/4">
  <div className="aspect-w-1 aspect-h-1 rounded-lg overflow-hidden bg-gray-100">
    <img
      src={getImageUrl(item.filepath)}
      alt={item.name}
      className="object-cover w-full h-full"
      onError={(e) => {
        console.error('Image load error for:', item.filepath);
        e.target.src = '/placeholder/400/320';
        e.target.onerror = null;
      }}
    />
  </div>
</div>
  
                        {/* Product Details */}
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                {item.name || t.unnamedProduct}
                              </h3>
                              <p className="text-sm text-gray-600 mb-4">
                                {item.category || t.uncategorized}
                              </p>
                            </div>
                            <button
                              onClick={() => handleDeleteProduct(item.productId)}
                              className="p-2 text-gray-400 hover:text-red-500 transition-colors hover:bg-red-50 rounded-full"
                              title={t.remove}
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
  
                          <div className="flex flex-wrap items-center justify-between mt-4">
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center border border-gray-300 rounded-lg">
                                <button
                                  onClick={() => handleDecreaseQuantity(item)}
                                  className="p-2 hover:bg-gray-100 rounded-l-lg transition"
                                  title="Decrease quantity"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                                <span className="px-4 py-2 text-gray-700 font-medium">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() => handleIncreaseQuantity(item)}
                                  className="p-2 hover:bg-gray-100 rounded-r-lg transition"
                                  title="Increase quantity"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
  
                            <div className="text-right">
                              <p className="text-lg font-semibold text-gray-900">
                                ₹{(parseFloat(item.price) * item.quantity).toFixed(2)}
                              </p>
                              <p className="text-sm text-gray-500">
                                ₹{parseFloat(item.price).toFixed(2)} each
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
  
            
            {/* Shopping Benefits - Left Aligned when cart has items */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
            >
              {shoppingBenefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300"
                >
                  <benefit.icon className="w-8 h-8 text-blue-600 mb-4" />
                  <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              ))}
            </motion.div>

            {/* FAQ Section - Left Aligned when cart has items */}
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">{t.faq}</h2>
                <button
                  onClick={() => setShowFAQ(!showFAQ)}
                  className="text-blue-600 hover:text-blue-700 transition p-2 rounded-full hover:bg-blue-50"
                >
                  <HelpCircle className="w-6 h-6" />
                </button>
              </div>

              <AnimatePresence>
                {showFAQ && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {FAQ.map((item, index) => (
                      <div key={index} className="border-b pb-4 last:border-b-0 last:pb-0">
                        <h3 className="font-medium text-gray-900 mb-2">{item.question}</h3>
                        <p className="text-gray-600">{item.answer}</p>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-4">
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-xl shadow-md p-6 sticky top-24"
            >
              <h2 className="text-xl font-semibold mb-6">{t.orderSummary}</h2>

              <div className="space-y-4">
                <div className="flex justify-between text-gray-600">
                  <span>{t.subtotal}</span>
                  <span>₹{calculateSubtotal().toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-gray-600">
                  <span>{t.tax}</span>
                  <span>₹{getTaxAmount().toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-gray-600">
                  <span>{t.estimatedShipping}</span>
                  <span className="text-green-600 font-medium">{t.freeShipping}</span>
                </div>

                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between items-center text-lg font-semibold">
                    <span>{t.total}</span>
                    <span className="text-blue-600">₹{getFinalTotal().toFixed(2)}</span>
                  </div>
                </div>

                <Link
                  to="/checkout"
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center justify-center mt-6 font-medium"
                >
                  {t.checkout}
                </Link>

                <Link
                  to="/product"
                  className="w-full text-center text-blue-600 hover:text-blue-700 transition duration-200 mt-4 block"
                >
                  {t.continueShopping}
                </Link>
              </div>

              {/* Secure Checkout Info */}
              <div className="mt-6 pt-6 border-t space-y-3">
                <div className="flex items-center text-gray-600">
                  <ShieldCheck className="w-5 h-5 mr-2 text-green-600" />
                  <span>{t.secureCheckout}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="w-5 h-5 mr-2 text-blue-600" />
                  <span>{t.guaranteedDelivery}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </div>
  </div>
);
};

export default Cart;